// ============================================================================
// IMPORT/EXPORT API HELPERS
// Compatible con el apiClient basado en fetch del proyecto
// CORREGIDO: rutas diferentes según el módulo
// - Catálogos básicos: /export y /template
// - Módulos de facturación: /export/excel y /export/template
// ============================================================================

// Keys para localStorage (igual que en client.ts)
const ACCESS_TOKEN_KEY = 'imi_access_token';
const API_BASE_URL = '/api/v1';

// Tipos
export type ImportMode = 'create_only' | 'update_only' | 'create_or_update';

export interface PreviewRow {
  rowNumber: number;
  data: Record<string, any>;
  status: 'new' | 'update' | 'error' | 'skip';
  errors: string[];
  existingId?: string;
}

export interface ImportPreview {
  valid: boolean;
  totalRows: number;
  newRows: number;
  updateRows: number;
  errorRows: number;
  rows: PreviewRow[];
}

export interface ImportResult {
  success: boolean;
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

// Helper para obtener headers de autenticación
function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {};

  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Añadir siteId si existe
  const siteId = localStorage.getItem('currentSiteId');
  if (siteId) {
    headers['X-Site-Id'] = siteId;
  }

  return headers;
}

// Helper para descargar un blob como archivo
function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// Helper para extraer filename del header Content-Disposition
function getFilenameFromResponse(response: Response, defaultName: string): string {
  const contentDisposition = response.headers.get('Content-Disposition');
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (match) return match[1];
  }
  return defaultName;
}

/**
 * Crear funciones de import/export para un módulo específico
 * @param modulePath - Ruta del módulo (ej: 'specialties')
 * @param useExcelSuffix - Si true, usa /export/excel y /export/template; si false, usa /export y /template
 */
export function createImportExportApi(modulePath: string, useExcelSuffix = true) {
  const baseUrl = `${API_BASE_URL}/${modulePath}`;
  
  // Rutas según el tipo de módulo
  const exportPath = useExcelSuffix ? '/export/excel' : '/export';
  const templatePath = useExcelSuffix ? '/export/template' : '/template';

  return {
    /**
     * Exportar datos a Excel (descarga directa)
     */
    export: async (includeInactive = false): Promise<void> => {
      const url = includeInactive
        ? `${baseUrl}${exportPath}?includeInactive=true`
        : `${baseUrl}${exportPath}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}`);
      }

      const blob = await response.blob();
      const filename = getFilenameFromResponse(
        response,
        `${modulePath}_${new Date().toISOString().split('T')[0]}.xlsx`,
      );
      downloadBlob(blob, filename);
    },

    /**
     * Descargar plantilla vacía
     */
    downloadTemplate: async (): Promise<void> => {
      const url = `${baseUrl}${templatePath}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}`);
      }

      const blob = await response.blob();
      const filename = getFilenameFromResponse(
        response,
        `plantilla_${modulePath}.xlsx`,
      );
      downloadBlob(blob, filename);
    },

    /**
     * Preview de importación (validar archivo sin importar)
     */
    preview: async (file: File): Promise<ImportPreview> => {
      const url = `${baseUrl}/import/preview`;

      const formData = new FormData();
      formData.append('file', file);

      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}`);
      }

      return response.json();
    },

    /**
     * Importar datos desde Excel
     */
    import: async (file: File, mode: ImportMode): Promise<ImportResult> => {
      const url = `${baseUrl}/import`;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', mode);

      const headers = getAuthHeaders();

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}`);
      }

      return response.json();
    },
  };
}

// ============================================================================
// APIs específicas para cada módulo
// ============================================================================

// CATÁLOGOS BÁSICOS - usan /export y /template (sin sufijo /excel)
export const specialtiesImportExport = createImportExportApi('specialties', false);
export const subspecialtiesImportExport = createImportExportApi('subspecialties', false);
export const treatmentGroupsImportExport = createImportExportApi('treatment-groups', false);
export const societiesImportExport = createImportExportApi('societies', false);
export const rejectionTypesImportExport = createImportExportApi('rejection-types', false);

// MÓDULOS DE FACTURACIÓN - usan /export/excel y /export/template
export const medicalActsImportExport = createImportExportApi('medical-acts', true);
export const insuranceCompaniesImportExport = createImportExportApi('insurance-companies', true);
export const policiesImportExport = createImportExportApi('policies', true);
export const tariffsImportExport = createImportExportApi('tariffs', true);

// ============================================================================
// BULK IMPORT - Carga masiva de tarifa completa con actos
// ============================================================================

export interface BulkImportActPreview {
  rowNumber: number;
  medicalActCode: string;
  medicalActName?: string;
  price: number;
  status: 'valid' | 'error';
  errors: string[];
}

export interface BulkImportTariffPreview {
  insuranceCompanyCode: string;
  policyCode: string;
  tariffCode: string;
  tariffName: string;
  validFrom: string;
  validTo?: string;
  actsCount: number;
  validActsCount: number;
  errorActsCount: number;
  acts: BulkImportActPreview[];
  errors: string[];
  isValid: boolean;
}

export interface BulkImportPreview {
  valid: boolean;
  tariffs: BulkImportTariffPreview[];
  totalRows: number;
  errors: string[];
}

export interface BulkImportResult {
  success: boolean;
  tariffsCreated: number;
  tariffsUpdated: number;
  actsCreated: number;
  actsUpdated: number;
  errors: string[];
}

export const tariffsBulkImport = {
  /**
   * Descargar plantilla para carga masiva
   */
  downloadTemplate: async (): Promise<void> => {
    const url = `${API_BASE_URL}/tariffs/bulk-import/template`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        window.location.href = '/login';
        throw new Error('Sesión expirada');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error ${response.status}`);
    }

    const blob = await response.blob();
    const filename = getFilenameFromResponse(
      response,
      'plantilla_tarifa_completa.xlsx',
    );
    downloadBlob(blob, filename);
  },

  /**
   * Preview de carga masiva
   */
  preview: async (file: File): Promise<BulkImportPreview> => {
    const url = `${API_BASE_URL}/tariffs/bulk-import/preview`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        window.location.href = '/login';
        throw new Error('Sesión expirada');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error ${response.status}`);
    }

    return response.json();
  },

  /**
   * Ejecutar carga masiva
   */
  import: async (file: File, mode: ImportMode = 'create_or_update'): Promise<BulkImportResult> => {
    const url = `${API_BASE_URL}/tariffs/bulk-import?mode=${mode}`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        window.location.href = '/login';
        throw new Error('Sesión expirada');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error ${response.status}`);
    }

    return response.json();
  },
};
